import React, { useState, useEffect } from 'react';
import samplesService from '../../services/samplesService';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Badge,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  VerifiedUser,
  Assignment,
  CheckCircle,
  Warning,
  Error,
  Refresh,
  Visibility,
  Person,
  ExpandMore,
  Science,
  Assessment,
  Description,
  Timeline,
} from '@mui/icons-material';

interface Sample {
  id: string;
  sample_id: string;
  client_name: string;
  type_echantillon: string;
  statut: string;
  laboratories_completed: string[];
  priority: 'NORMALE' | 'ELEVEE' | 'URGENTE';
  date_reception: string;
  validation_required_at: string;
  results: any[];
  quality_flags: string[];
}

interface ValidationStats {
  pending_validation: number;
  validated_today: number;
  rejected_today: number;
  overdue_validation: number;
  avg_validation_time_hours: number;
  quality_issues: number;
}

interface ValidationServiceDashboardProps {
  userRole: 'SUPERVISEUR';
  userId: string;
}

const ValidationServiceDashboard: React.FC<ValidationServiceDashboardProps> = ({
  userRole,
  userId,
}) => {
  const [samples, setSamples] = useState<Sample[]>([]);
  const [stats, setStats] = useState<ValidationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [validationDecision, setValidationDecision] = useState<'APPROVE' | 'REJECT' | 'REQUEST_RETEST'>('APPROVE');
  const [validationNotes, setValidationNotes] = useState('');

  useEffect(() => {
    loadDashboardData();
    // Set up auto-refresh every 10 seconds (optimized for real-time updates)
    const interval = setInterval(loadDashboardData, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load actual data from API
      const samplesResponse = await samplesService.getSamples(1, 100, {
        status: ['VALIDATION_SERVICE']
      });
      
      const validationSamples = samplesResponse.samples || [];
      
      // Calculate real statistics
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const validatedToday = validationSamples.filter(s => {
        const validationDate = new Date(s.date_validation || '');
        return validationDate >= today;
      }).length;
      
      const stats: ValidationStats = {
        pending_validation: validationSamples.length,
        validated_today: validatedToday,
        rejected_today: 0, // TODO: Track rejected samples
        overdue_validation: validationSamples.filter(s => s.priorite === 'URGENTE').length,
        avg_validation_time_hours: 2.5, // TODO: Calculate from actual data
        quality_issues: 0, // TODO: Track quality issues
      };

      setSamples(validationSamples as any);
      setStats(stats);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartValidation = (sample: Sample) => {
    setSelectedSample(sample);
    setValidationDialogOpen(true);
    setValidationDecision('APPROVE');
    setValidationNotes('');
  };

  const handleCompleteValidation = async () => {
    if (!selectedSample) return;

    try {
      // TODO: Implement actual API call to complete validation
      console.log(`Validation ${validationDecision} for sample ${selectedSample.sample_id}`);
      console.log('Notes:', validationNotes);
      
      setValidationDialogOpen(false);
      setSelectedSample(null);
      loadDashboardData(); // Refresh data
    } catch (error) {
      console.error('Error completing validation:', error);
    }
  };

  const getPriorityColor = (priority: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (priority) {
      case 'URGENTE': return 'error';
      case 'ELEVEE': return 'warning';
      default: return 'default';
    }
  };

  const getQualityFlagColor = (flag: string) => {
    switch (flag) {
      case 'HIGH_VALUE_DETECTED': return 'warning';
      case 'INCONSISTENT_RESULTS': return 'error';
      case 'LOW_PRECISION': return 'info';
      default: return 'default';
    }
  };

  const getQualityFlagLabel = (flag: string) => {
    const labels = {
      'HIGH_VALUE_DETECTED': 'Valeur élevée détectée',
      'INCONSISTENT_RESULTS': 'Résultats incohérents',
      'LOW_PRECISION': 'Précision faible',
    };
    return labels[flag] || flag;
  };

  const isOverdue = (validationRequiredAt: string) => {
    return new Date(validationRequiredAt) < new Date(Date.now() - 4 * 3600000); // 4 hours overdue
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Service de Validation - Chargement...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <VerifiedUser color="primary" />
          Service de Validation
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadDashboardData}
          >
            Actualiser
          </Button>
          <Chip
            label={`Rôle: ${userRole}`}
            color="primary"
            icon={<Person />}
          />
        </Box>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  En Attente de Validation
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.pending_validation}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Validés Aujourd'hui
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.validated_today}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Rejetés Aujourd'hui
                </Typography>
                <Typography variant="h4" color="error.main">
                  {stats.rejected_today}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Temps Moyen (heures)
                </Typography>
                <Typography variant="h4" color="info.main">
                  {stats.avg_validation_time_hours}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Alerts */}
      {stats && stats.overdue_validation > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <strong>{stats.overdue_validation}</strong> échantillon(s) en retard de validation nécessitent une attention immédiate.
        </Alert>
      )}

      {stats && stats.quality_issues > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <strong>{stats.quality_issues}</strong> échantillon(s) avec des signaux qualité nécessitent une attention particulière.
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
          <Tab label="En Attente" icon={<Badge badgeContent={stats?.pending_validation} color="warning"><Assignment /></Badge>} />
          <Tab label="Validés" icon={<Badge badgeContent={stats?.validated_today} color="success"><CheckCircle /></Badge>} />
          <Tab label="Signaux Qualité" icon={<Badge badgeContent={stats?.quality_issues} color="error"><Warning /></Badge>} />
        </Tabs>
      </Box>

      {/* Sample Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID Échantillon</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Laboratoires</TableCell>
                  <TableCell>Priorité</TableCell>
                  <TableCell>Signaux Qualité</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {samples
                  .filter(sample => {
                    if (selectedTab === 0) return sample.statut === 'VALIDATION_SERVICE';
                    if (selectedTab === 1) return sample.statut === 'GENERATION_CERTIFICAT';
                    if (selectedTab === 2) return sample.quality_flags.length > 0;
                    return true;
                  })
                  .map((sample) => (
                    <TableRow key={sample.id} sx={{ backgroundColor: isOverdue(sample.validation_required_at) ? '#ffebee' : 'inherit' }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {sample.sample_id}
                        </Typography>
                        {isOverdue(sample.validation_required_at) && (
                          <Chip label="EN RETARD" size="small" color="error" sx={{ mt: 0.5 }} />
                        )}
                      </TableCell>
                      <TableCell>{sample.client_name}</TableCell>
                      <TableCell>
                        <Chip label={sample.type_echantillon} size="small" />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {sample.laboratories_completed.map((lab, index) => (
                            <Chip key={index} label={lab} size="small" variant="outlined" color="success" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={sample.priority}
                          size="small"
                          color={getPriorityColor(sample.priority)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {sample.quality_flags.map((flag, index) => (
                            <Chip 
                              key={index} 
                              label={getQualityFlagLabel(flag)} 
                              size="small" 
                              color={getQualityFlagColor(flag)}
                            />
                          ))}
                          {sample.quality_flags.length === 0 && (
                            <Chip label="OK" size="small" color="success" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={sample.statut.replace('_', ' ')}
                          size="small"
                          color={sample.statut === 'VALIDATION_SERVICE' ? 'warning' : 'success'}
                        />
                      </TableCell>
                      <TableCell>
                        {sample.statut === 'VALIDATION_SERVICE' ? (
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<VerifiedUser />}
                            onClick={() => handleStartValidation(sample)}
                          >
                            Valider
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Visibility />}
                            onClick={() => setSelectedSample(sample)}
                          >
                            Voir Détails
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Validation Dialog */}
      <Dialog open={validationDialogOpen} onClose={() => setValidationDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Validation des Résultats - {selectedSample?.sample_id}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Informations Échantillon
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <Typography><strong>Client:</strong> {selectedSample?.client_name}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography><strong>Type:</strong> {selectedSample?.type_echantillon}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography><strong>Date Réception:</strong> {selectedSample?.date_reception ? new Date(selectedSample.date_reception).toLocaleDateString() : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography><strong>Priorité:</strong> {selectedSample?.priority}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" gutterBottom>
              Résultats d'Analyse
            </Typography>
            {selectedSample?.results.map((result, index) => (
              <Accordion key={index}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography>
                    <strong>{result.laboratory}</strong> - {result.method}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography><strong>Valeur:</strong> {result.value} {result.unit}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography><strong>Statut:</strong> {result.status}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Chip label={result.status} size="small" color="success" />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}

            <Divider sx={{ my: 2 }} />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Décision de Validation</InputLabel>
              <Select 
                value={validationDecision} 
                onChange={(e) => setValidationDecision(e.target.value as typeof validationDecision)}
              >
                <MenuItem value="APPROVE">Approuver - Résultats validés</MenuItem>
                <MenuItem value="REJECT">Rejeter - Résultats non conformes</MenuItem>
                <MenuItem value="REQUEST_RETEST">Demander nouveau test</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes de Validation"
              value={validationNotes}
              onChange={(e) => setValidationNotes(e.target.value)}
              placeholder="Ajoutez vos commentaires sur la validation..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationDialogOpen(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleCompleteValidation} 
            variant="contained"
            color={validationDecision === 'APPROVE' ? 'success' : validationDecision === 'REJECT' ? 'error' : 'warning'}
          >
            Confirmer Validation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ValidationServiceDashboard; 